import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
 
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function firstName(name: string | null | undefined): string {
  if (!name) return ""
  return name.trim().split(/\s+/)[0] || ""
}

export function diffNewNotifications<T extends { _id: string }>(
  knownIds: Set<string> | null,
  items: T[]
): { knownIds: Set<string>; newOnes: T[] } {
  if (knownIds === null) {
    return { knownIds: new Set(items.map((n) => n._id)), newOnes: [] }
  }
  const newOnes = items.filter((n) => !knownIds.has(n._id))
  const next = new Set(knownIds)
  for (const n of newOnes) next.add(n._id)
  return { knownIds: next, newOnes }
}

export function groupReservationsByPassenger<T extends { passengerId?: string; name?: string; avatar?: string | null }>(
  reservations: T[]
): Array<{ passengerId: string; name?: string; avatar?: string | null; count: number; reservations: T[] }> {
  const map = new Map<
    string,
    { passengerId: string; name?: string; avatar?: string | null; count: number; reservations: T[] }
  >()
  for (const r of reservations) {
    const key = r.passengerId || r.name || "?"
    const existing = map.get(key)
    if (existing) {
      existing.count += 1
      existing.reservations.push(r)
    } else {
      map.set(key, {
        passengerId: r.passengerId ?? "",
        name: r.name,
        avatar: r.avatar,
        count: 1,
        reservations: [r],
      })
    }
  }
  return Array.from(map.values())
}

export const PRICE_PER_KM = 0.2102//0.25
export const STOP_SURCHARGE = 1.10

export function parseDistanceToKm(text: string): number {
  const normalized = text.replace(/,/g, "").trim()
  const kmMatch = normalized.match(/([\d.]+)\s*km/i)
  if (kmMatch) return parseFloat(kmMatch[1])
  const mMatch = normalized.match(/([\d.]+)\s*m/i)
  if (mMatch) return parseFloat(mMatch[1]) / 1000
  const numMatch = normalized.match(/([\d.]+)/)
  if (numMatch) return parseFloat(numMatch[1])
  return 0
}

export function ceilMinutesTo5(minutes: number): number {
  return Math.ceil(minutes / 5) * 5
}

export function ceilTimeTo5(timeStr: string | null | undefined): string {
  if (!timeStr) return ""
  const [h, m] = timeStr.split(":").map(Number)
  if (!Number.isInteger(h) || !Number.isInteger(m) || h < 0 || h > 23 || m < 0 || m > 59) return timeStr
  const total = ceilMinutesTo5(h * 60 + m)
  const hh = Math.floor(total / 60) % 24
  const mm = total % 60
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`
}

export function formatDistanceKm(distanceStr: string | null | undefined): string {
  if (!distanceStr) return ""
  const kmMatch = String(distanceStr).match(/([\d.,]+)\s*km/i)
  if (kmMatch) return `${Math.ceil(parseFloat(kmMatch[1].replace(",", ".")))} km`
  return String(distanceStr)
}

export function ceilDurationTo5(durationStr: string | null | undefined): string {
  if (!durationStr) return ""
  const hourMatch = String(durationStr).match(/(\d+)\s*h/)
  const minMatch = String(durationStr).match(/(\d+)\s*min/)
  if (!hourMatch && !minMatch) return String(durationStr)
  const hours = hourMatch ? parseInt(hourMatch[1]) : 0
  const mins = minMatch ? parseInt(minMatch[1]) : 0
  const total = ceilMinutesTo5(hours * 60 + mins)
  const h = Math.floor(total / 60)
  const m = total % 60
  return h > 0 ? (m > 0 ? `${h}h ${m}min` : `${h}h`) : `${m}min`
}

export function decodePolyline(encoded: string): Array<{ lat: number; lng: number }> {
  const points: Array<{ lat: number; lng: number }> = []
  let index = 0
  let lat = 0
  let lng = 0

  while (index < encoded.length) {
    let b: number
    let shift = 0
    let result = 0
    do {
      b = encoded.charCodeAt(index++) - 63
      result |= (b & 0x1f) << shift
      shift += 5
    } while (b >= 0x20)
    lat += result & 1 ? ~(result >> 1) : result >> 1

    shift = 0
    result = 0
    do {
      b = encoded.charCodeAt(index++) - 63
      result |= (b & 0x1f) << shift
      shift += 5
    } while (b >= 0x20)
    lng += result & 1 ? ~(result >> 1) : result >> 1

    points.push({ lat: lat / 1e5, lng: lng / 1e5 })
  }

  return points
}

